import { Document, isMap, isSeq, parseDocument, type YAMLMap } from 'yaml'

export type DefinitionIconEdit = { kind: 'definition_icon'; target: 'capability' | 'skill'; id: string; iconKey: string }
export type DefinitionView = { id: string; label: string; iconKey: string; target: DefinitionIconEdit['target'] }
const TARGETS = { capability: { path: ['capability_registry', 'capabilities'], key: 'capability_id' },
  skill: { path: ['skill_definitions'], key: 'skill_id' } }
const text = (value: unknown) => typeof value === 'string' ? value : ''

export const definitionView = (content: string): DefinitionView[] => {
  const doc = parseDocument(content)
  if (doc.errors.length) throw new Error('配置格式有误')
  return Object.entries(TARGETS).flatMap(([target, source]) => {
    const items = doc.getIn(source.path, true)
    if (!isSeq(items)) return []
    return items.items.filter(isMap).map((item) => ({ id: text(item.get(source.key)),
      label: text(item.get('label')), iconKey: text(item.get('icon_key')), target: target as DefinitionIconEdit['target'] }))
  })
}

/** An icon belongs to one definition, never to whichever blueprint happens to be open. */
export const editDefinitionIcon = (content: string, command: DefinitionIconEdit): string => {
  if (command.iconKey && !/^\S{1,128}$/.test(command.iconKey)) throw new Error('图标标识不可用')
  const doc = parseDocument(content, { keepSourceTokens: true })
  if (doc.errors.length) throw new Error('配置格式有误')
  const target = TARGETS[command.target]
  const items = doc.getIn(target.path, true)
  if (!isSeq(items)) throw new Error('图标来源声明不存在')
  const matches = items.items.filter((item) => isMap(item) && item.get(target.key) === command.id)
  if (matches.length !== 1 || !isMap(matches[0])) throw new Error('图标来源标识不存在或重复')
  const node = matches[0]
  if (text(node.get('icon_key')) === command.iconKey) return content
  if (command.iconKey) node.set('icon_key', command.iconKey)
  else node.delete('icon_key')
  return replaceMapping(content, node)
}

/** Shared AST shaping preserves all bytes outside the one edited mapping. */
export const replaceMapping = (content: string, node: YAMLMap): string => {
  if (!node.range) throw new Error('无法安全更新此配置')
  const [start, end] = node.range
  const column = start - (content.lastIndexOf('\n', start - 1) + 1)
  let serialized: string
  try { JSON.parse(content); serialized = JSON.stringify(node.toJSON()) }
  catch { serialized = new Document(node).toString({ lineWidth: 0 }).replace(/\n$/, '') }
  const replacement = serialized.split('\n').map((line, index) => index ? ' '.repeat(column) + line : line).join('\n')
  const newline = node.flow && !content.slice(start, end).endsWith('\n') ? '' : '\n'
  const result = content.slice(0, start) + replacement + newline + content.slice(end)
  if (parseDocument(result).errors.length) throw new Error('无法安全更新此配置，请使用源码编辑')
  return result
}
